import logger from './logger';

// Global notification state
let notificationListeners = [];
let notificationCounter = 0;

// Subscribe to notification events
export const subscribeToNotifications = (callback) => {
  notificationListeners.push(callback);
  return () => {
    notificationListeners = notificationListeners.filter(listener => listener !== callback);
  };
};

// Emit notification to all listeners
const emitNotification = (notification) => {
  notificationListeners.forEach(listener => listener(notification));
};

// Enhanced error handling utility
export class AppError extends Error {
  constructor(message, type = 'generic', severity = 'error', context = {}, originalError = null) {
    super(message);
    this.name = 'AppError';
    this.type = type;
    this.severity = severity;
    this.context = context;
    this.originalError = originalError;
    this.timestamp = new Date().toISOString();
    this.id = this.generateErrorId();
    this.userMessage = this.generateUserMessage();
    this.suggestions = this.generateSuggestions();
  }

  generateErrorId() {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  generateUserMessage() {
    const userMessages = {
      'network': 'Connection problem. Please check your internet and try again.',
      'authentication': 'Your session has expired. Please log in again.',
      'authorization': 'You don\'t have permission to access this feature.',
      'validation': 'Please check your input and try again.',
      'server': 'Server error. Please try again in a few minutes.',
      'timeout': 'Request timed out. Please try again.',
      'not_found': 'The requested resource was not found.',
      'rate_limit': 'Too many requests. Please wait before trying again.',
      'maintenance': 'We\'re performing maintenance. Please try again later.',
      'split_error': 'There was an issue loading content. Please refresh the page.',
      'api_error': 'We encountered an issue processing your request.',
      'generic': 'Something went wrong. Please try again or contact support.'
    };

    return userMessages[this.type] || userMessages.generic;
  }

  generateSuggestions() {
    const suggestionMap = {
      'network': [
        'Check your internet connection',
        'Try refreshing the page',
        'Wait a few seconds and try again'
      ],
      'authentication': [
        'Click "Log In" to sign in again',
        'Clear your browser cache and cookies',
        'Try logging in from a new browser tab'
      ],
      'validation': [
        'Double-check all required fields',
        'Ensure email addresses are properly formatted',
        'Check that passwords meet requirements'
      ],
      'server': [
        'Wait a few minutes and try again',
        'Refresh the page',
        'Contact support if the problem persists'
      ],
      'split_error': [
        'Refresh the page',
        'Clear your browser cache',
        'Try using a different browser'
      ]
    };

    return suggestionMap[this.type] || [];
  }

  toLogFormat() {
    return {
      id: this.id,
      type: this.type,
      severity: this.severity,
      message: this.message,
      userMessage: this.userMessage,
      context: this.context,
      timestamp: this.timestamp,
      originalError: this.originalError ? {
        name: this.originalError.name,
        message: this.originalError.message,
        stack: this.originalError.stack
      } : null
    };
  }
}

// Global error handler
export class ErrorHandler {
  constructor() {
    this.errorQueue = [];
    this.isOnline = navigator.onLine;
    this.setupGlobalHandlers();
  }

  setupGlobalHandlers() {
    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const error = new AppError(
        'Unhandled promise rejection',
        'generic',
        'error',
        { originalEvent: event.reason },
        event.reason
      );
      this.handleError(error);
      event.preventDefault(); // Prevent console error
    });

    // Catch JavaScript errors
    window.addEventListener('error', (event) => {
      const error = new AppError(
        `Script error: ${event.message}`,
        'generic',
        'error',
        { 
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        },
        event.error
      );
      this.handleError(error);
    });

    // Monitor network status
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.retryQueuedErrors();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  handleError(error, options = {}) {
    // Convert regular errors to AppErrors
    if (!(error instanceof AppError)) {
      error = new AppError(
        error.message || 'Unknown error',
        options.type || 'generic',
        options.severity || 'error',
        options.context || {},
        error
      );
    }

    // Log the error
    logger.log(error.severity, error.message, error.context, error.originalError);

    // Handle based on severity
    switch (error.severity) {
      case 'critical':
        this.handleCriticalError(error);
        break;
      case 'error':
        this.handleRegularError(error);
        break;
      case 'warning':
        this.handleWarning(error);
        break;
      default:
        this.handleInfo(error);
    }

    return error;
  }

  handleCriticalError(error) {
    // For critical errors, show modal that requires user action
    this.showErrorModal(error);
  }

  handleRegularError(error) {
    // For regular errors, show notification but allow continued use
    this.showErrorNotification(error);
  }

  handleWarning(error) {
    // For warnings, show toast notification
    this.showErrorToast(error);
  }

  handleInfo(error) {
    // For info, just log (already done in handleError)
    console.info('Info:', error.userMessage);
  }

  // These methods will be implemented by the UI layer
  showErrorModal(error) {
    // Dispatch custom event for UI to handle
    window.dispatchEvent(new CustomEvent('app:error:modal', { 
      detail: error.toLogFormat() 
    }));
  }

  showErrorNotification(error) {
    window.dispatchEvent(new CustomEvent('app:error:notification', { 
      detail: error.toLogFormat() 
    }));
  }

  showErrorToast(error) {
    window.dispatchEvent(new CustomEvent('app:error:toast', { 
      detail: error.toLogFormat() 
    }));
  }

  // API error handling
  handleApiError(response, requestContext = {}) {
    let errorType = 'api_error';
    let severity = 'error';
    let message = 'API request failed';

    if (!this.isOnline) {
      errorType = 'network';
      message = 'Network connection lost';
    } else if (response.status >= 500) {
      errorType = 'server';
      severity = 'error';
      message = 'Server error occurred';
    } else if (response.status === 401) {
      errorType = 'authentication';
      severity = 'warning';
      message = 'Authentication required';
    } else if (response.status === 403) {
      errorType = 'authorization';
      severity = 'warning';
      message = 'Access denied';
    } else if (response.status === 404) {
      errorType = 'not_found';
      severity = 'warning';
      message = 'Resource not found';
    } else if (response.status === 429) {
      errorType = 'rate_limit';
      severity = 'warning';
      message = 'Rate limit exceeded';
    } else if (response.status >= 400) {
      errorType = 'validation';
      severity = 'warning';
      message = 'Request validation failed';
    }

    const error = new AppError(
      message,
      errorType,
      severity,
      {
        ...requestContext,
        status: response.status,
        url: response.url || requestContext.url
      }
    );

    return this.handleError(error);
  }

  // Utility method to handle the specific split error from dashboard
  handleSplitError(originalError, context = {}) {
    const error = new AppError(
      'Cannot read properties of undefined (reading \'split\')',
      'split_error',
      'error',
      {
        ...context,
        component: 'dashboard',
        action: 'data_processing'
      },
      originalError
    );

    return this.handleError(error);
  }

  retryQueuedErrors() {
    // Retry failed API calls when back online
    while (this.errorQueue.length > 0) {
      const queuedError = this.errorQueue.shift();
      if (queuedError.retryFunction) {
        try {
          queuedError.retryFunction();
        } catch (e) {
          logger.error('Failed to retry queued operation', { originalError: queuedError }, e);
        }
      }
    }
  }
}

// Create global instance
export const errorHandler = new ErrorHandler();

// Utility functions for common error scenarios
export const createNetworkError = (originalError, context = {}) => {
  return new AppError(
    'Network request failed',
    'network',
    'warning',
    context,
    originalError
  );
};

export const createValidationError = (message, context = {}) => {
  return new AppError(
    message,
    'validation',
    'warning',
    context
  );
};

export const createAuthError = (message = 'Authentication failed', context = {}) => {
  return new AppError(
    message,
    'authentication',
    'warning',
    context
  );
};

// Replace window.alert with better error handling
export const showUserFriendlyAlert = (message, type = 'error') => {
  const error = new AppError(message, type, type === 'error' ? 'error' : 'warning');
  errorHandler.handleError(error);
};

export const showToastNotification = (title, message, type = 'info', duration = 5000) => {
  const notification = {
    id: `toast-${++notificationCounter}`,
    title,
    message,
    type,
    timestamp: new Date().toISOString()
  };

  logger.info('Showing toast notification', notification);
  emitNotification(notification);

  // Auto-dismiss after duration
  if (duration > 0) {
    setTimeout(() => {
      emitNotification({ type: 'dismiss', id: notification.id });
    }, duration);
  }

  return notification.id;
};

export default errorHandler;
